import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, ArrowUpRight, Server, TestTube2, Briefcase, Sparkles,
  Lightbulb, Activity, Shield, Wifi, Globe, Radar, Signal,
  ClipboardList, Compass, Rocket, BarChart3, Headphones,
  CheckCircle2, Quote, Plus, Minus
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import SectionHeader from "@/components/SectionHeader";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const services = [
  { icon: Server, title: "Managed", accent: "Services", desc: "End-to-end BSS operations — billing, CRM, mediation, charging and ongoing L1–L3 support tailored for telecom operators." },
  { icon: TestTube2, title: "End-to-End", accent: "Testing", desc: "Pre-ATP, ATP, UAT and regression cycles that validate every integration across your stack before go-live." },
  { icon: Lightbulb, title: "Telecom", accent: "Strategy", desc: "Advisory on BSS transformation, digital readiness and operational optimisation for modern operators." },
];

const logos = [
  { icon: Globe, label: "NovaTel" },
  { icon: Activity, label: "PulseNet" },
  { icon: Wifi, label: "AirLink" },
  { icon: Signal, label: "Veloxa" },
  { icon: Radar, label: "Orbitwave" },
  { icon: Shield, label: "Sentrion" },
  { icon: Server, label: "Corefiber" },
];

const process = [
  { step: "01", title: "Discovery & Planning", desc: "Mapping business goals and architecting a clear delivery roadmap tailored for your stack." },
  { step: "02", title: "Implementation", desc: "Hands-on configuration, system integration and rollout with minimal disruption." },
  { step: "03", title: "Testing & QA", desc: "Rigorous Pre-ATP, ATP and UAT cycles with performance tuning and quality gates." },
  { step: "04", title: "Managed Support", desc: "L1–L3 managed services and enhancements that keep you ahead of demand." },
];

const reasons = [
  { title: "20+ Years Experience", desc: "Deep telecom lineage across GSM, LTE and 5G operators.", icon: BarChart3 },
  { title: "Telecom Domain Expertise", desc: "Specialists in BSS, OSS and revenue assurance — not generalists.", icon: Lightbulb },
  { title: "End-to-End Solutions", desc: "From strategy to go-live to 24×7 managed ops under one roof.", icon: Server },
  { title: "Scalable & Reliable Systems", desc: "Architectures engineered to grow with subscribers and services.", icon: Shield },
  { title: "Client-Centric Approach", desc: "Long partnerships built on transparency and measurable outcomes.", icon: Sparkles },
];

const testimonials = [
  { name: "Ritika Malhotra", role: "VP Engineering, NovaTel", quote: "Moxizo took our BSS modernisation from a fuzzy ambition to a rock-solid rollout. The testing rigour was unmatched." },
  { name: "Arun Deshpande", role: "Head of PMO, PulseNet", quote: "Genuine telecom veterans. Every sprint had visible outcomes and no surprise regressions at UAT." },
  { name: "Fatima Al-Qadri", role: "CTO, AirLink Mobility", quote: "Their managed services team feels like an extension of our own. Proactive, precise and always accountable." },
  { name: "Daniel Okafor", role: "Director Strategy, Orbitwave", quote: "From consulting to product launch, Moxizo delivered a repeatable playbook our team still uses today." },
];

const faqs = [
  { q: "What telecom services do you provide?", a: "We offer BSS managed services, end-to-end testing (Pre-ATP, ATP, UAT), project management, brand & product development, and strategic telecom consulting for operators worldwide." },
  { q: "What is BSS support?", a: "Business Support Systems cover billing, CRM, mediation, charging and partner management. Our BSS support runs L1 to L3, keeps revenue flowing and evolves the stack with your roadmap." },
  { q: "Do you offer testing services?", a: "Yes — Pre-ATP validation, ATP with operator sign-off, UAT cycles, regression and non-functional performance testing across the full BSS/OSS estate." },
  { q: "How can we get started?", a: "Reach out via the contact form or book a consultation call. We scope a discovery session within 48 hours and share a proposal shortly after." },
];

const blogPosts = [
  {
    title: "The Future of 5G Monetization for Modern Operators",
    category: "Strategy",
    date: "April 15, 2026",
    image: "/images/blog1.png",
    slug: "5g-monetization"
  },
  {
    title: "Optimising BSS Workflows for Scale and Precision",
    category: "BSS Ops",
    date: "April 10, 2026",
    image: "/images/blog2.png",
    slug: "bss-optimization"
  },
  {
    title: "Securing Telecom Infrastructure in a Digital-First World",
    category: "Security",
    date: "April 05, 2026",
    image: "/images/blog3.png",
    slug: "telecom-security"
  },
  {
    title: "How ATP Testing Reduces Rollout Regressions by 40%",
    category: "Testing",
    date: "March 28, 2026",
    image: "/images/blog1.png",
    slug: "atp-testing-benefits"
  }
];

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [api, setApi] = useState();
  const statsRef = useRef(null);
  const [animatedStats, setAnimatedStats] = useState({
    experience: 0,
    rollouts: 0,
    countries: 0,
    sla: 0,
  });

  useEffect(() => {
    if (!api) return;
    const intervalId = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 4000);
    return () => clearInterval(intervalId);
  }, [api]);

  useEffect(() => {
    const statsNode = statsRef.current;
    if (!statsNode) return;

    let rafId;
    const animateStats = () => {
      const duration = 1600;
      const start = performance.now();
      const targets = {
        experience: 20,
        rollouts: 40,
        countries: 12,
        sla: 99.9,
      };

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        setAnimatedStats({
          experience: Math.round(targets.experience * eased),
          rollouts: Math.round(targets.rollouts * eased),
          countries: Math.round(targets.countries * eased),
          sla: Number((targets.sla * eased).toFixed(1)),
        });

        if (progress < 1) {
          rafId = requestAnimationFrame(step);
        }
      };

      rafId = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        animateStats();
        observer.disconnect();
      },
      { threshold: 0.4 }
    );

    observer.observe(statsNode);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32 text-center">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[500px] bg-gradient-radial from-cyan-500/10 via-transparent to-transparent blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6">
          <span className="section-label animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="sm:hidden">BSS · 20+ Years</span>
            <span className="hidden sm:inline md:hidden">Telecom · BSS · 20+ Years</span>
            <span className="hidden md:inline">Telecom Consulting · BSS · Testing · 20+ Years</span>
          </span>

          <h1 className="mt-6 mx-auto font-semibold tracking-tight leading-[1.08] animate-fade-up delay-100 text-5xl sm:text-6xl md:text-7xl lg:text-7xl max-w-[14ch] sm:max-w-[20ch] md:max-w-[26ch]">
            Empowering<br className="sm:hidden" /> Telecom Innovation<br className="hidden sm:inline md:hidden" /> <span className="sm:hidden">with</span><span className="hidden sm:inline">with{" "}</span>
            <span className="gradient-text">Expertise &<br className="sm:hidden" /> Precision</span>
          </h1>

          <p className="mt-6 md:mt-7 mx-auto text-base sm:text-base md:text-lg text-gray-400 leading-relaxed animate-fade-up delay-200 max-w-[30ch] sm:max-w-xl md:max-w-2xl">
            <span className="md:hidden">20+ years of BSS and testing expertise for global telecom operators.</span>
            <span className="hidden md:inline">20+ years of telecom consulting experience delivering BSS solutions, managed services, and end-to-end testing for global operators.</span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto animate-fade-up delay-300">
            <Link to="/services" data-testid="hero-cta-primary" className="btn-primary w-full sm:w-auto justify-center">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" data-testid="hero-cta-secondary" className="btn-ghost w-full sm:w-auto justify-center">
              Contact Us <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stats strip */}
          <div ref={statsRef} className="mt-24 grid grid-cols-4 md:grid-cols-4 gap-3 md:gap-y-10 max-w-5xl mx-auto animate-fade-up delay-400">
            {[
              { k: `${animatedStats.experience}+`, v: "Years Experience" },
              { k: `${animatedStats.rollouts}+`, v: "Operator Rollouts" },
              { k: `${animatedStats.countries}`, v: "Countries Served" },
              { k: `${animatedStats.sla}%`, v: "Delivery SLA" },
            ].map((s) => (
              <div key={s.v} className="text-center">
                <div className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight gradient-text leading-none">{s.k}</div>
                <div className="mt-3 md:mt-4 text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.12em] md:tracking-[0.22em] text-gray-400">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section className="py-16 border-y border-white/5 bg-[#0c0c0e]" data-testid="client-logos-section">
        <p className="text-center text-xs uppercase tracking-[0.3em] text-gray-500 mb-10">
          Trusted by operators across the globe
        </p>
        <Marquee gradient gradientColor="#0c0c0e" gradientWidth={120} speed={40} pauseOnHover>
          {logos.concat(logos).map((l, i) => (
            <div key={i} className="logo-tile flex items-center gap-3 mx-10 text-gray-400">
              <l.icon className="w-6 h-6" strokeWidth={1.5} />
              <span className="text-xl font-medium tracking-wide">{l.label}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="What We Do"
            title="Our"
            gradientWord="Services"
            subtitle="From BSS operations to testing and strategy — we cover the entire telecom lifecycle so your team can focus on the customers and the services they actually ship."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group relative card-base p-8 flex flex-col overflow-hidden"
              >
                <span className="absolute top-6 right-6 text-xs font-semibold tracking-[0.2em] text-gray-600 group-hover:text-cyan-400/70 transition-colors">
                  0{i + 1}
                </span>

                <div className="icon-circle w-14 h-14 mb-7">
                  <s.icon className="w-5 h-5" />
                </div>

                <h3 className="text-2xl md:text-3xl font-medium leading-tight tracking-tight">
                  {s.title} <span className="gradient-text">{s.accent}</span>
                </h3>
                <p className="mt-4 text-sm text-gray-400 leading-relaxed flex-1">{s.desc}</p>

                <div className="mt-8 pt-6 border-t border-white/5">
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-sm font-medium text-cyan-400 group-hover:gap-3 transition-all"
                  >
                    <span className="w-8 h-8 rounded-full border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-400 group-hover:border-cyan-400 transition-all">
                      <ArrowUpRight className="w-3.5 h-3.5 text-cyan-400 group-hover:text-black transition-colors" />
                    </span>
                    Learn more
                  </Link>
                </div>

                <div className="pointer-events-none absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-cyan-400/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* PROCESS TIMELINE */}
      <section className="py-24 md:py-32 bg-[#0c0c0e] border-y border-white/5" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="How We Work"
            title="Our"
            gradientWord="Process"
            subtitle="A delivery-first engagement model — from discovery to steady-state support. Every stage ships something tangible, measurable and owned by a named lead."
          />

          <div className="relative mt-24 md:mt-32">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-12">
              {process.map((p, i) => (
                <div key={p.step} className="relative text-center group" data-testid={`process-step-${i}`}>
                  {/* Large background number */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-8xl md:text-9xl font-bold text-white/[0.03] select-none group-hover:text-cyan-400/[0.05] transition-colors duration-500 pointer-events-none">
                    {p.step}
                  </div>
                  
                  <div className="relative z-10">
                    <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white group-hover:text-cyan-400 transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-sm md:text-base text-gray-400 leading-relaxed max-w-xs mx-auto">
                      {p.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="About Moxizo"
            title="About"
            gradientWord="Moxizo"
            subtitle="With over 20+ years of experience in the telecom domain, Moxizo delivers reliable consulting services tailored for telecom operators. We help businesses streamline operations, optimize systems, and deliver better customer experiences."
          />

          <div className="grid md:grid-cols-2 gap-14 items-center">
            <div className="flex flex-col justify-center py-4 order-2 md:order-1">
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                At Moxizo, we believe in more than just providing services; we build lasting partnerships. Our deep-rooted expertise in the telecom sector allows us to understand the unique challenges faced by operators today, from legacy stack modernization to the seamless rollout of next-gen services. We combine technical rigour with a customer-first philosophy to deliver outcomes that matter.
              </p>
              <ul className="space-y-5">
                {["BSS modernisation & managed ops", "End-to-end testing with measurable quality gates", "PMO governance trusted by CXOs"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-base text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400" /> {t}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Link to="/about" data-testid="about-read-more" className="btn-primary inline-flex">
                  Explore Our Expertise <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="relative order-1 md:order-2">
              <div className="absolute -inset-6 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-600/10 blur-2xl" />
              <div className="relative card-base overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1594345558880-804452ebc6b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwZmliZXIlMjBvcHRpYyUyMGJsdWV8ZW58MHx8fHwxNzc3Mjc5MjAzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Fiber optic abstract"
                  className="w-full h-[480px] object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/20 border border-cyan-400/30 flex items-center justify-center">
                    <Signal className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Telecom-native consulting</div>
                    <div className="text-xs text-gray-400">Built by engineers who ran carriers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="Why Choose Us"
            title="The"
            gradientWord={<span>Moxizo <span className="hidden md:inline">Advantage</span></span>}
            subtitle="Senior telecom operators, testing veterans and PMO leaders — no junior hand-offs, no generic playbooks. Every engagement is run by the people who will deliver it."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reasons.slice(0, 3).map((r, i) => (
              <div
                key={r.title}
                data-testid={`reason-${i}`}
                className="group relative p-8 bg-[#0c0c0e] border border-white/5 hover:border-cyan-400/30 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-4 opacity-[0.02] group-hover:opacity-[0.07] transition-opacity pointer-events-none transform translate-x-4 -translate-y-4">
                  <r.icon className="w-32 h-32" />
                </div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-full bg-cyan-400/5 flex items-center justify-center mb-7 border border-cyan-400/10 group-hover:bg-cyan-400 group-hover:border-cyan-400 group-hover:text-black transition-all duration-500">
                    <r.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4 tracking-tight">{r.title}</h3>
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed">{r.desc}</p>
                </div>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-cyan-400 to-blue-600 group-hover:w-full transition-all duration-700 ease-in-out" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#0c0c0e] border-y border-white/5" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="Voices"
            title="Client"
            gradientWord="Stories"
            subtitle="Real words from the operators, CTOs and PMO heads we've partnered with across BSS transformations, managed services programs and multi-vendor rollouts."
          />

          <Marquee speed={30} pauseOnHover={true} gradient={false}>
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className="w-[320px] md:w-[450px] h-[260px] md:h-[280px] mx-3 card-base p-8 flex flex-col justify-between"
                data-testid={`testimonial-${i}`}
              >
                <div>
                  <Quote className="w-6 h-6 text-cyan-400/70" />
                  <p className="mt-5 text-sm text-gray-300 leading-relaxed line-clamp-4">"{t.quote}"</p>
                </div>
                <div className="mt-6 pt-5 border-t border-white/5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400/30 to-blue-600/30 border border-white/10 flex items-center justify-center text-sm font-medium shrink-0">
                    {t.name.split(" ").map(x => x[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-xs text-gray-500 truncate">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* BLOG SECTION */}
      <section className="pt-24 md:pt-32 pb-12" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="Insights"
            title="Latest"
            gradientWord="Articles"
            subtitle="Deep dives into telecom strategy, BSS transformation, and operational excellence from the veterans who live it every day."
          />

          <div className="mt-16">
            <Marquee speed={40} pauseOnHover={true} gradient={false}>
              {blogPosts.map((post, i) => (
                <Link
                  key={i}
                  to={`/blog`}
                  className="w-[300px] md:w-[400px] mx-4 group"
                  data-testid={`blog-card-${i}`}
                >
                  <div className="card-base overflow-hidden flex flex-col h-full">
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-cyan-400 text-black text-[10px] font-bold uppercase tracking-wider rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-xs text-gray-500 mb-3">{post.date}</div>
                      <h3 className="text-lg md:text-xl font-semibold leading-tight group-hover:text-cyan-400 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-medium text-cyan-400 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                        Read Article <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </Marquee>
          </div>

          <div className="mt-12">
            <Link 
              to="/blog" 
              className="w-full border border-white/5 bg-white/[0.02] hover:border-cyan-400/40 hover:bg-cyan-400/5 py-5 flex items-center justify-center gap-3 text-sm font-medium tracking-widest uppercase transition-all group"
            >
              View All Blogs <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pt-12 pb-24 md:pb-32" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="FAQ"
            title="Common"
            gradientWord={<span><span className="hidden md:inline">Questions</span><span className="md:hidden">QA</span></span>}
            subtitle="Quick answers to what operators usually ask before we kick things off. If yours isn't here, drop us a line — we'll reply within one business day."
          />

          <div className="w-full divide-y divide-white/5 border-t border-white/5">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <button
                  key={f.q}
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  data-testid={`faq-item-${i}`}
                  className="w-full text-left py-6 group"
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center transition-all ${open ? "bg-cyan-400 text-black border-cyan-400" : "border-white/10 text-gray-300 group-hover:border-cyan-400/40"}`}>
                      {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                    <span className="flex-1 text-base md:text-lg font-medium">{f.q}</span>
                  </div>
                  <div className={`overflow-hidden transition-all duration-500 ${open ? "max-h-40 mt-4" : "max-h-0"}`}>
                    <p className="pl-12 text-sm text-gray-400 leading-relaxed">{f.a}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-12" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative card-base overflow-hidden p-10 md:p-16 text-center">
            <div className="absolute inset-0 grid-pattern opacity-70" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60%] h-[300px] bg-cyan-500/10 blur-3xl rounded-full" />
            <div className="relative">
              <span className="section-label">Let's Talk</span>
              <h2 className="mt-5 text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl mx-auto">
                <span className="md:hidden">Let's build the <span className="gradient-text">future</span> together.</span>
                <span className="hidden md:inline">Let's build the <span className="gradient-text">future of telecom</span> together.</span>
              </h2>
              <p className="mt-6 max-w-xl mx-auto text-gray-400">
                Whether you need a BSS overhaul or a 24×7 managed partner — we're ready when you are.
              </p>
              <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
                <Link to="/contact" data-testid="cta-contact" className="btn-primary">Contact Us <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/contact" data-testid="cta-book" className="btn-ghost">Book Consultation <ArrowUpRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
